import React, { useCallback, useEffect, useRef } from 'react';

import { useMediaQuery } from '@/hooks/useResponsive';
import { isMobileByUA } from '@/utils/ua';

import ControlCard from './ControlCard';
import ControlDecorations from './ControlDecorations';
import styles from './index.module.less';
import portal_functionintro_image1_cn from './portal_functionintro_image1_cn.png';
import portal_functionintro_image2_cn from './portal_functionintro_image2_cn.png';
import portal_functionintro_image3_cn from './portal_functionintro_image3_cn.png';
import portal_functionintro_image4_cn from './portal_functionintro_image4_cn.png';

const defaultImages = [
  portal_functionintro_image1_cn,
  portal_functionintro_image2_cn,
  portal_functionintro_image3_cn,
  portal_functionintro_image4_cn,
];

const ControlSphere: React.FC = () => {
  const isLess1000 = useMediaQuery('(max-width: 1000px)');
  const isMobile = isMobileByUA() || isLess1000;

  const cardData = [
    {
      title: '尽在掌控',
      items: [
        {
          title: '每一行变更，尽在你手',
          description: '每一次生成的变更，都由你把关。逐行或逐文件审查确认，方可提交。',
        },
        {
          title: '内置高级模型，随心选择',
          description: '即刻上手，感受行业领先代码大模型的非凡实力。',
        },
      ],
    },
    {
      title: '快人一步',
      items: [
        {
          title: '设计即代码',
          description: '原生内置 Figma 工具，轻松将设计稿转换为高还原度生产级代码。',
        },
        {
          title: '实时可视化预览调试',
          description: '一键启动实时预览，选择页面元素或控制台错误即可快速迭代。',
        },
      ],
    },
  ];

  // 计算总的item数量（当前为4个item）
  const totalItems = cardData.reduce((total, card) => total + card.items.length, 0);

  // 当前选中的item全局索引（0-3循环）
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const timerRef = React.useRef<NodeJS.Timeout>();
  const animationTimeoutRef = React.useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLElement>(null);

  // 将卡片内的局部索引转换为全局索引
  // 例如：第二个卡片的第一个item的全局索引为2（0+1+0+1）
  const getGlobalIndex = (cardIndex: number, itemIndex: number) => {
    let globalIndex = 0;
    for (let i = 0; i < cardIndex; i++) {
      globalIndex += cardData[i].items.length;
    }
    return globalIndex + itemIndex;
  };

  // 基于新的映射规则生成进度节点配置
  // 大节点对应分组标题，中等节点对应具体items
  const generateProgressNodes = () => {
    const nodes: Array<{
      id: string;
      type: 'large' | 'medium' | 'small';
      isActive: boolean;
      position: { x: number; y: number };
    }> = [];

    let currentY = 0; // 初始顶部偏移

    cardData.forEach((card, cardIndex) => {
      // 为每个分组标题添加一个大节点
      const groupNodeY = currentY + 6; // 与标题中心对齐

      const isGroupActive = Math.floor(selectedIndex / 2) === cardIndex;

      nodes.push({
        id: `group-${card.title.toLowerCase().replace(/\s+/g, '-')}`,
        type: 'large',
        isActive: isGroupActive,
        position: {
          x: 7.42969, // 大节点使用原始SVG中的x坐标
          y: groupNodeY,
        },
      });

      // 添加标题高度 (subTitle 约40px高度 + 24px margin-bottom)
      currentY += 12;

      // 为每个item添加中等节点
      card.items.forEach((_, itemIndex) => {
        const globalIndex = getGlobalIndex(cardIndex, itemIndex);

        // 计算节点位置 - 让节点与卡片中心对齐
        const cardHeight = 90; // 卡片的实际高度 (padding + content)
        const nodeY = currentY + cardHeight; // 中等节点高度的一半居中对齐

        nodes.push({
          id: `${card.title.toLowerCase().replace(/\s+/g, '-')}-item-${itemIndex}`,
          type: 'medium',
          isActive: globalIndex === selectedIndex,
          position: {
            x: 13.4316, // 中等节点使用原始SVG中的x坐标
            y: nodeY,
          },
        });

        // 调试信息：节点位置计算

        // 累加卡片高度 + margin
        currentY += cardHeight + 23; // 23px 是 margin-bottom
      });

      // 组与组之间的额外间距
      currentY += 45;
    });

    return nodes;
  };

  // 动态生成进度组，基于新的映射规则
  // 进度展示规则：
  // - 大节点(large): 对应分组标题 ("Always Under Control", "Build Frontend Faster")
  // - 中等节点(medium): 对应具体的items ("Own Every Change", "Built-in Advanced Models", "Turn Figma into Code", "Interactive Preview")
  // - 连接线: 渐变色和大小变化的方块，连接各个节点，根据激活状态显示不同颜色
  const progressGroups = [
    {
      id: 'sphere-control-progress',
      nodes: generateProgressNodes(),
    },
  ];

  // 调试信息：生成的进度组

  // 触发动画效果
  const triggerAnimation = useCallback(() => {
    setIsAnimating(true);
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, 600); // 与CSS动画时长保持一致
  }, []);

  // 启动定时器，每5秒切换到下一个item
  const startTimer = useCallback(() => {
    // 只有在可视区域内才启动定时器
    if (!isInView) return;

    timerRef.current = setInterval(() => {
      setSelectedIndex((prev) => {
        const newIndex = (prev + 1) % totalItems;
        triggerAnimation();
        return newIndex;
      });
    }, 3500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  // 停止定时器（移动端用户交互时调用）
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  };

  // 重置定时器（桌面端用户交互时调用）
  const resetTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    startTimer();
  };

  // 使用 Intersection Observer 监听组件是否在可视区域内
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // 当组件30%可见时触发
        rootMargin: '0px 0px -100px 0px', // 提前100px触发
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // 当组件进入可视区域时启动定时器，离开时停止定时器
  useEffect(() => {
    if (isInView) {
      startTimer();
    } else {
      stopTimer();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isInView, startTimer]);

  // 处理卡片点击事件，更新选中状态并根据设备类型处理定时器
  const handleCardClick = (globalIndex: number) => {
    setSelectedIndex(globalIndex);
    triggerAnimation();

    // 移动端手动切换后停止定时器，桌面端重置定时器
    if (isMobileByUA()) {
      stopTimer();
    } else {
      resetTimer();
    }
  };

  // 获取当前显示的背景图片
  const getCurrentBackgroundImage = () => {
    return defaultImages[selectedIndex % defaultImages.length];
  };

  return (
    <section ref={containerRef} className={styles.container}>
      {/* 标题部分 */}
      <div className={styles.title}>
        <div className={styles.titleMain}>掌控每一处&nbsp;·&nbsp;</div>
        <div className={styles.titleSub}>精准每一步</div>
      </div>

      <div className={`${styles.contentArea} ${isMobile ? styles.mobileLayout : ''}`}>
        {/* 移动端时：右侧面板在上方展示 */}
        {isMobile && (
          <div
            className={`${styles.rightPanel} ${styles.mobileRightPanel} ${isAnimating ? styles.animating : ''}`}
          >
            <img
              src={getCurrentBackgroundImage()}
              alt="Feature preview"
              className={styles.panelImage}
            />
          </div>
        )}

        {/* 左侧面板 */}
        <div className={`${styles.leftPanel} ${isMobile ? styles.mobileLeftPanel : ''}`}>
          <div className={`relative flex w-full flex-col ${isMobile ? '' : 'pl-16'}`}>
            {/* 装饰元素 - 移动端时隐藏 */}
            {!isMobile && <ControlDecorations groups={progressGroups} />}

            {/* 卡片列表 */}
            <div>
              {cardData.map((card, cardIndex) => (
                <div key={cardIndex}>
                  <h2
                    className={`${styles.subTitle} ${Math.floor(selectedIndex / 2) === cardIndex ? styles.selected : ''}`}
                  >
                    {' '}
                    {card.title}
                  </h2>
                  {card.items.map((item, itemIndex) => {
                    const globalIndex = getGlobalIndex(cardIndex, itemIndex);
                    return (
                      <ControlCard
                        key={itemIndex}
                        title={item.title}
                        description={item.description}
                        state={globalIndex === selectedIndex ? 'selected' : 'normal'}
                        onClick={() => handleCardClick(globalIndex)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 桌面端时：右侧面板在右边展示 */}
        {!isMobile && (
          <div className={`${styles.rightPanel} ${isAnimating ? styles.animating : ''}`}>
            <img
              src={getCurrentBackgroundImage()}
              alt="Feature preview"
              className={styles.panelImage}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default ControlSphere;
